import { csvSafe } from './csv';
import type { AreaType, Field } from '../types';
import { getAreaTypeDefinition, getAreaTypeLabel } from './domain';

export interface DxfShape {
  name: string;
  geometry: { type: 'Polygon'; coordinates: [number, number][][] };
  areaType?: AreaType;
  kind?: 'field' | 'zone';
  notes?: string;
}

export interface ExtendedDxfOptions {
  includeLabels?: boolean;
  labelHeightMeters?: number;
  poiCircleRadiusMeters?: number;
  layers?: ('marker' | 'zoner' | 'biotopplan' | 'pois')[];
}

/**
 * WKT definition of ETRS89 / UTM zone 32N (EPSG:25832) for QGIS and CAD .prj sidecar files.
 */
export const ETRS89_UTM32N_PRJ =
  'PROJCS["ETRS89 / UTM zone 32N",GEOGCS["ETRS89",DATUM["European_Terrestrial_Reference_System_1989",SPHEROID["GRS 1980",6378137,298.257222101,AUTHORITY["EPSG","7019"]],AUTHORITY["EPSG","6258"]],PRIMEM["Greenwich",0,AUTHORITY["EPSG","8901"]],UNIT["degree",0.0174532925199433,AUTHORITY["EPSG","9122"]],AUTHORITY["EPSG","4258"]],PROJECTION["Transverse_Mercator"],PARAMETER["latitude_of_origin",0],PARAMETER["central_meridian",9],PARAMETER["scale_factor",0.9996],PARAMETER["false_easting",500000],PARAMETER["false_northing",0],UNIT["metre",1,AUTHORITY["EPSG","9001"]],AXIS["Easting",EAST],AXIS["Northing",NORTH],AUTHORITY["EPSG","25832"]]';

export function generatePrj(): string {
  return ETRS89_UTM32N_PRJ;
}

/**
 * Converts WGS84 longitude and latitude to UTM Zone 32N coordinates (EPSG:25832).
 * This implementation uses the Transverse Mercator projection formula.
 *
 * @param lng Longitude in decimal degrees.
 * @param lat Latitude in decimal degrees.
 * @returns [x, y] representing Easting and Northing in meters.
 */
export function wgs84ToUtm32n(lng: number, lat: number): [number, number] {
  const a = 6378137.0; // WGS84 semi-major axis
  const f = 1 / 298.257223563; // WGS84 flattening
  const k0 = 0.9996; // Central meridian scale factor for UTM
  const lon0 = 9.0; // Central meridian for UTM Zone 32N

  const eSq = 2 * f - f * f;
  const ePrimeSq = eSq / (1 - eSq);

  const latRad = (lat * Math.PI) / 180.0;
  const lonRad = (lng * Math.PI) / 180.0;
  const lon0Rad = (lon0 * Math.PI) / 180.0;

  const N = a / Math.sqrt(1 - eSq * Math.sin(latRad) * Math.sin(latRad));
  const T = Math.tan(latRad) * Math.tan(latRad);
  const C = ePrimeSq * Math.cos(latRad) * Math.cos(latRad);
  const A = Math.cos(latRad) * (lonRad - lon0Rad);

  const M =
    a *
    ((1 - eSq / 4 - (3 * eSq * eSq) / 64 - (5 * eSq * eSq * eSq) / 256) * latRad -
      ((3 * eSq) / 8 + (3 * eSq * eSq) / 32 + (45 * eSq * eSq * eSq) / 1024) *
        Math.sin(2 * latRad) +
      ((15 * eSq * eSq) / 256 + (45 * eSq * eSq * eSq) / 1024) * Math.sin(4 * latRad) -
      ((35 * eSq * eSq * eSq) / 3072) * Math.sin(6 * latRad));

  const x =
    k0 *
      N *
      (A +
        ((1 - T + C) * Math.pow(A, 3)) / 6 +
        ((5 - 18 * T + T * T + 72 * C - 58 * ePrimeSq) * Math.pow(A, 5)) / 120) +
    500000.0;

  const y =
    k0 *
    (M +
      N *
        Math.tan(latRad) *
        (Math.pow(A, 2) / 2 +
          ((5 - T + 9 * C + 4 * C * C) * Math.pow(A, 4)) / 24 +
          ((61 - 58 * T + T * T + 600 * C - 330 * ePrimeSq) * Math.pow(A, 6)) / 720));

  return [x, y];
}

/**
 * Generates an ASCII DXF (R12 format) string for a given list of shapes.
 * Preserved for backwards compatibility with existing rover downloads.
 */
export function generateDxf(shapes: DxfShape[]): string {
  const lines: string[] = [];

  // DXF header R12 with CRS in HEADER section
  lines.push(
    '0',
    'SECTION',
    '2',
    'HEADER',
    '9',
    '$ACADVER',
    '1',
    'AC1009',
    '9',
    '$PROJECTNAME',
    '1',
    'Plotwise - EPSG:25832 (UTM Zone 32N)',
    '999',
    'CRS: EPSG:25832 (UTM Zone 32N)',
    '0',
    'ENDSEC',
  );
  lines.push('0', 'SECTION', '2', 'ENTITIES');

  for (const shape of shapes) {
    if (shape.geometry.type !== 'Polygon') continue;

    const rawLayer = shape.name.replace(/[^a-zA-Z0-9_-]/g, '_') || 'Layer_0';
    const typePrefix = shape.areaType
      ? getAreaTypeDefinition(shape.areaType, shape.kind).label.replace(/[^a-zA-Z0-9_-]/g, '_')
      : '';
    const layerName = typePrefix ? `${typePrefix}_${rawLayer}` : rawLayer;

    for (const ring of shape.geometry.coordinates) {
      if (ring.length === 0) continue;

      // Start POLYLINE
      lines.push('0', 'POLYLINE', '8', layerName, '66', '1');
      // Elevation (Z) and Flags
      lines.push('10', '0.0', '20', '0.0', '30', '0.0', '70', '1'); // 1 = closed polyline

      // Vertices
      for (const [lng, lat] of ring) {
        const [x, y] = wgs84ToUtm32n(lng, lat);
        lines.push('0', 'VERTEX', '8', layerName);
        lines.push('10', x.toFixed(3), '20', y.toFixed(3), '30', '0.0');
      }

      // End SEQEND
      lines.push('0', 'SEQEND', '8', layerName);
    }
  }

  lines.push('0', 'ENDSEC');
  lines.push('0', 'EOF');

  return lines.join('\r\n');
}

/**
 * Standard ACI (AutoCAD Color Index) Layer definition
 */
interface DxfLayerDef {
  name: string;
  color: number; // ACI 1-255
}

const EXTENDED_DXF_LAYERS: readonly DxfLayerDef[] = [
  { name: 'MARKER', color: 3 }, // Grøn
  { name: 'MARKER_TEKST', color: 7 }, // Hvid/Sort
  { name: 'ZONER', color: 30 }, // Orange/Brun
  { name: 'ZONER_TEKST', color: 30 },
  { name: 'BIOTOPPLAN', color: 2 }, // Gul
  { name: 'BIOTOPPLAN_TEKST', color: 2 },
  { name: 'POIS_SKYDETAARN', color: 40 }, // Rav / Amber
  { name: 'POIS_FODERTOENDE', color: 3 }, // Grøn
  { name: 'POIS_VILDTKAMERA', color: 5 }, // Blå
  { name: 'POIS_SALTSTEN', color: 6 }, // Magenta
  { name: 'POIS_SENSOR', color: 1 }, // Rød
  { name: 'POIS_PUNKT', color: 40 }, // Rav
  { name: 'POIS_TEKST', color: 7 }, // Hvid/Sort
] as const;

function isZoneEntity(item: { kind?: string; areaType?: string; notes?: string }): boolean {
  return (
    item.kind === 'zone' ||
    item.areaType === 'jagtomraade' ||
    Boolean(typeof item.notes === 'string' && item.notes.startsWith('[OMRÅDE:jagtomraade]'))
  );
}

function isPoiEntity(item: { notes?: string }): boolean {
  return Boolean(typeof item.notes === 'string' && item.notes.trimStart().startsWith('[POI:'));
}

function parsePoiInfo(notes?: string): { poiType: string; direction?: string; label: string } {
  if (!notes || !notes.trimStart().startsWith('[POI:')) {
    return { poiType: 'PUNKT', label: notes || '' };
  }
  const match = notes
    .trimStart()
    .match(/^\[POI:([a-z0-9_-]+)(?:(?::|\|dir:|\|)([a-z0-9æøå,_\s+-]+))?\]\s*([\s\S]*)$/i);
  if (!match) return { poiType: 'PUNKT', label: notes };
  const raw = match[1].toLowerCase();
  const dir = match[2]?.trim();
  const clean = match[3]?.trim() || '';

  let poiType = 'PUNKT';
  if (raw === 'skydetaarn') poiType = 'SKYDETAARN';
  else if (raw === 'fodertoende') poiType = 'FODERTOENDE';
  else if (raw === 'vildtkamera') poiType = 'VILDTKAMERA';
  else if (raw === 'saltsten') poiType = 'SALTSTEN';
  else if (raw === 'jordmaaler' || raw === 'faeldesensor' || raw === 'sensor') poiType = 'SENSOR';

  return { poiType, direction: dir, label: clean };
}

/**
 * Calculates planar centroid [x, y] in meters for a sequence of UTM coordinates.
 */
function calculateUtmCentroid(pts: [number, number][]): [number, number] {
  if (pts.length === 0) return [0, 0];
  let sumX = 0;
  let sumY = 0;
  for (const [x, y] of pts) {
    sumX += x;
    sumY += y;
  }
  return [sumX / pts.length, sumY / pts.length];
}

/**
 * Calculates planar area in hectares for a UTM coordinate ring.
 */
function calculateUtmArea(ring: [number, number][]): number {
  if (ring.length < 3) return 0;
  let sum = 0;
  for (let i = 0; i < ring.length - 1; i++) {
    sum += ring[i][0] * ring[i + 1][1] - ring[i + 1][0] * ring[i][1];
  }
  return Math.abs(sum) / 20000;
}

/**
 * Generates an extended, multi-layer CAD-standard ASCII DXF (AutoCAD R12 AC1009) string.
 *
 * Includes:
 * - Proper HEADER with $INSUNITS = 6 (meters), $MEASUREMENT = 1 (metric), $EXTMIN, $EXTMAX
 * - TABLES section with LAYER definitions and official ACI colors
 * - Separated geometry layers (MARKER, ZONER, BIOTOPPLAN, POIS_*)
 * - Distinct text annotation layers (MARKER_TEKST, ZONER_TEKST, BIOTOPPLAN_TEKST, POIS_TEKST)
 * - POINT and CIRCLE entities for POIs
 * - Closed POLYLINE boundaries (70 = 1) for polygons
 */
export function generateExtendedDxf(
  fields: (Field | DxfShape)[],
  options: ExtendedDxfOptions = {},
): string {
  const includeLabels = options.includeLabels ?? true;
  const labelHeight = options.labelHeightMeters ?? 3.5;
  const poiRadius = options.poiCircleRadiusMeters ?? 3.5;
  const selectedLayers = new Set(options.layers ?? ['marker', 'zoner', 'biotopplan', 'pois']);

  // 1. Gather all UTM coordinates to compute bounding box
  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;

  interface ProcessedPolygon {
    name: string;
    category: 'marker' | 'zoner' | 'biotopplan';
    rings: [number, number][][];
    areaHa: number;
    centroid: [number, number];
    areaTypeLabel: string;
  }

  interface ProcessedPoi {
    name: string;
    poiType: string;
    center: [number, number];
    direction?: string;
    notes: string;
  }

  const polyItems: ProcessedPolygon[] = [];
  const poiItems: ProcessedPoi[] = [];

  for (const f of fields) {
    if (!f || !f.geometry) continue;

    if (isPoiEntity(f as any)) {
      if (!selectedLayers.has('pois')) continue;
      const { poiType, direction, label } = parsePoiInfo((f as any).notes);
      let center: [number, number] | null = null;

      const geom = f.geometry as any;
      if (geom.type === 'Point' && Array.isArray(geom.coordinates)) {
        center = wgs84ToUtm32n(Number(geom.coordinates[0]), Number(geom.coordinates[1]));
      } else if (geom.type === 'Polygon' && Array.isArray(geom.coordinates[0])) {
        const ring = (geom.coordinates[0] as [number, number][]).map(([lng, lat]) =>
          wgs84ToUtm32n(lng, lat),
        );
        center = calculateUtmCentroid(ring);
      }

      if (center) {
        if (center[0] < minX) minX = center[0];
        if (center[0] > maxX) maxX = center[0];
        if (center[1] < minY) minY = center[1];
        if (center[1] > maxY) maxY = center[1];
        poiItems.push({
          name: f.name || 'Jagt-POI',
          poiType,
          center,
          direction,
          notes: label,
        });
      }
      continue;
    }

    const isZone = isZoneEntity(f as any);
    const category: 'marker' | 'zoner' | 'biotopplan' = isZone ? 'zoner' : 'marker';
    if (!selectedLayers.has(category)) continue;

    if (f.geometry.type === 'Polygon' && Array.isArray(f.geometry.coordinates)) {
      const rings: [number, number][][] = [];
      for (const rawRing of f.geometry.coordinates) {
        if (!rawRing || rawRing.length === 0) continue;
        const ring: [number, number][] = rawRing.map(([lng, lat]) => wgs84ToUtm32n(lng, lat));
        const first = ring[0];
        const last = ring[ring.length - 1];
        if (first[0] !== last[0] || first[1] !== last[1]) {
          ring.push([first[0], first[1]]);
        }
        for (const [x, y] of ring) {
          if (x < minX) minX = x;
          if (x > maxX) maxX = x;
          if (y < minY) minY = y;
          if (y > maxY) maxY = y;
        }
        rings.push(ring);
      }

      if (rings.length > 0) {
        const exterior = rings[0];
        const areaHa = calculateUtmArea(exterior);
        const centroid = calculateUtmCentroid(exterior);
        const areaTypeLabel = f.areaType ? getAreaTypeLabel(f.areaType, f.kind) : 'Mark';

        polyItems.push({
          name: f.name || (isZone ? 'Jagtområde' : 'Mark'),
          category,
          rings,
          areaHa,
          centroid,
          areaTypeLabel,
        });
      }
    }
  }

  if (!Number.isFinite(minX)) {
    minX = 500000;
    maxX = 500100;
    minY = 6100000;
    maxY = 6100100;
  }

  const lines: string[] = [];

  // 1. HEADER SECTION
  lines.push(
    '0',
    'SECTION',
    '2',
    'HEADER',
    '9',
    '$ACADVER',
    '1',
    'AC1009',
    '9',
    '$INSUNITS',
    '70',
    '6', // 6 = Meters
    '9',
    '$MEASUREMENT',
    '70',
    '1', // 1 = Metric
    '9',
    '$EXTMIN',
    '10',
    minX.toFixed(3),
    '20',
    minY.toFixed(3),
    '30',
    '0.0',
    '9',
    '$EXTMAX',
    '10',
    maxX.toFixed(3),
    '20',
    maxY.toFixed(3),
    '30',
    '0.0',
    '9',
    '$PROJECTNAME',
    '1',
    'Plotwise - EPSG:25832 (UTM Zone 32N)',
    '999',
    'CRS: EPSG:25832 (UTM Zone 32N)',
    '0',
    'ENDSEC',
  );

  // 2. TABLES SECTION
  lines.push(
    '0',
    'SECTION',
    '2',
    'TABLES',
    // LTYPE Table
    '0',
    'TABLE',
    '2',
    'LTYPE',
    '70',
    '1',
    '0',
    'LTYPE',
    '2',
    'CONTINUOUS',
    '70',
    '0',
    '3',
    'Solid line',
    '72',
    '65',
    '73',
    '0',
    '40',
    '0.0',
    '0',
    'ENDTAB',
    // LAYER Table
    '0',
    'TABLE',
    '2',
    'LAYER',
    '70',
    String(EXTENDED_DXF_LAYERS.length),
  );

  for (const layer of EXTENDED_DXF_LAYERS) {
    lines.push(
      '0',
      'LAYER',
      '2',
      layer.name,
      '70',
      '0',
      '62',
      String(layer.color),
      '6',
      'CONTINUOUS',
    );
  }

  lines.push(
    '0',
    'ENDTAB',
    // STYLE Table
    '0',
    'TABLE',
    '2',
    'STYLE',
    '70',
    '1',
    '0',
    'STYLE',
    '2',
    'STANDARD',
    '70',
    '0',
    '40',
    '0.0',
    '41',
    '1.0',
    '50',
    '0.0',
    '71',
    '0',
    '42',
    '2.5',
    '3',
    'txt',
    '4',
    '',
    '0',
    'ENDTAB',
    '0',
    'ENDSEC',
  );

  // 3. ENTITIES SECTION
  lines.push('0', 'SECTION', '2', 'ENTITIES');

  // Emit Polygon Boundaries and Labels
  let biotopeNumber = 1;
  for (const poly of polyItems) {
    const layerName = poly.category === 'zoner' ? 'ZONER' : 'MARKER';
    const textLayerName = poly.category === 'zoner' ? 'ZONER_TEKST' : 'MARKER_TEKST';

    // Closed POLYLINE entity for each ring
    for (const ring of poly.rings) {
      lines.push('0', 'POLYLINE', '8', layerName, '66', '1');
      lines.push('10', '0.0', '20', '0.0', '30', '0.0', '70', '1'); // 70=1 is closed polyline

      for (const [x, y] of ring) {
        lines.push('0', 'VERTEX', '8', layerName);
        lines.push('10', x.toFixed(3), '20', y.toFixed(3), '30', '0.0');
      }

      lines.push('0', 'SEQEND', '8', layerName);
    }

    // Optional BIOTOPPLAN layer duplication if biotopplan is selected
    if (selectedLayers.has('biotopplan') && poly.category === 'marker') {
      for (const ring of poly.rings) {
        lines.push('0', 'POLYLINE', '8', 'BIOTOPPLAN', '66', '1');
        lines.push('10', '0.0', '20', '0.0', '30', '0.0', '70', '1');

        for (const [x, y] of ring) {
          lines.push('0', 'VERTEX', '8', 'BIOTOPPLAN');
          lines.push('10', x.toFixed(3), '20', y.toFixed(3), '30', '0.0');
        }

        lines.push('0', 'SEQEND', '8', 'BIOTOPPLAN');
      }

      if (includeLabels) {
        const [cx, cy] = poly.centroid;
        const bLabel = `#${biotopeNumber++} ${poly.areaTypeLabel}`;
        lines.push(
          '0',
          'TEXT',
          '8',
          'BIOTOPPLAN_TEKST',
          '10',
          cx.toFixed(3),
          '20',
          (cy + labelHeight * 1.6).toFixed(3),
          '30',
          '0.0',
          '40',
          labelHeight.toFixed(2),
          '1',
          bLabel,
          '72',
          '1', // Center
          '11',
          cx.toFixed(3),
          '21',
          (cy + labelHeight * 1.6).toFixed(3),
          '31',
          '0.0',
          '73',
          '2', // Middle
        );
      }
    }

    // Text labels with vertical offset so name and area/type do not collide
    if (includeLabels) {
      const [cx, cy] = poly.centroid;
      const line1Y = cy + labelHeight * 0.6;
      const line2Y = cy - labelHeight * 0.6;
      const areaLabel = `${poly.areaHa.toFixed(2)} ha (${poly.areaTypeLabel})`;

      // Line 1: Field/Zone Name
      lines.push(
        '0',
        'TEXT',
        '8',
        textLayerName,
        '10',
        cx.toFixed(3),
        '20',
        line1Y.toFixed(3),
        '30',
        '0.0',
        '40',
        labelHeight.toFixed(2),
        '1',
        poly.name,
        '72',
        '1', // Center
        '11',
        cx.toFixed(3),
        '21',
        line1Y.toFixed(3),
        '31',
        '0.0',
        '73',
        '2', // Middle
      );

      // Line 2: Area in ha and AreaType
      lines.push(
        '0',
        'TEXT',
        '8',
        textLayerName,
        '10',
        cx.toFixed(3),
        '20',
        line2Y.toFixed(3),
        '30',
        '0.0',
        '40',
        (labelHeight * 0.85).toFixed(2),
        '1',
        areaLabel,
        '72',
        '1', // Center
        '11',
        cx.toFixed(3),
        '21',
        line2Y.toFixed(3),
        '31',
        '0.0',
        '73',
        '2', // Middle
      );
    }
  }

  // Emit POI Points, Circles, and Text
  for (const poi of poiItems) {
    const layerName = `POIS_${poi.poiType}`;
    const [px, py] = poi.center;

    // 1. POINT entity
    lines.push('0', 'POINT', '8', layerName, '10', px.toFixed(3), '20', py.toFixed(3), '30', '0.0');

    // 2. CIRCLE entity for CAD visibility
    lines.push(
      '0',
      'CIRCLE',
      '8',
      layerName,
      '10',
      px.toFixed(3),
      '20',
      py.toFixed(3),
      '30',
      '0.0',
      '40',
      poiRadius.toFixed(2),
    );

    // 3. TEXT label placed next to the POI (offset +3m, +3m)
    if (includeLabels) {
      const poiText = poi.direction ? `${poi.name} (Retning: ${poi.direction})` : poi.name;
      const tx = px + poiRadius + 1.0;
      const ty = py + poiRadius + 1.0;

      lines.push(
        '0',
        'TEXT',
        '8',
        'POIS_TEKST',
        '10',
        tx.toFixed(3),
        '20',
        ty.toFixed(3),
        '30',
        '0.0',
        '40',
        (labelHeight * 0.8).toFixed(2),
        '1',
        poiText,
      );
    }
  }

  lines.push('0', 'ENDSEC');
  lines.push('0', 'EOF');

  return lines.join('\r\n');
}

/**
 * Generates a surveyor point list CSV for a given list of shapes.
 * Includes UTF-8 BOM, semicolon-separated, dot as decimal separator.
 */
export function generateSurveyorCsv(shapes: DxfShape[]): string {
  const lines: string[] = [];
  lines.push('Punkt;Nord;Øst;Kode;Type');

  let pointIndex = 1;

  for (const shape of shapes) {
    if (shape.geometry.type !== 'Polygon') continue;

    const code = csvSafe(shape.name.replaceAll(';', ',')); // Avoid breaking CSV and formulas
    const typeLabel = shape.areaType
      ? getAreaTypeLabel(shape.areaType, shape.kind)
      : shape.kind === 'zone'
        ? 'Jagtområde'
        : 'Mark';

    for (const ring of shape.geometry.coordinates) {
      for (const [lng, lat] of ring) {
        const [x, y] = wgs84ToUtm32n(lng, lat);
        lines.push(`${pointIndex};${y.toFixed(3)};${x.toFixed(3)};${code};${typeLabel}`);
        pointIndex++;
      }
    }
  }

  return '\uFEFF' + lines.join('\r\n');
}
